export interface NiceAxis {
  /** Верх оси Y. */
  max: number;
  /** Деления оси: 0, step, 2·step … max. */
  ticks: number[];
}

/**
 * Ось Y по данным для целых счётчиков: шаг 1/2/5 × 10^k, ~`targetTicks` делений.
 * При значениях 0–3 ось остаётся 0–4, а не растягивается до десятков.
 * Пустые/нулевые данные → 0…`minMax`.
 */
export function niceAxis(
  values: Array<number | null | undefined>,
  { minMax = 4, targetTicks = 4 }: { minMax?: number; targetTicks?: number } = {},
): NiceAxis {
  const max = Math.max(0, ...values.map((v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)));
  let step: number;
  let top: number;
  if (max <= minMax) {
    step = 1;
    top = minMax;
  } else {
    const raw = max / targetTicks;
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? 10 * magnitude;
    step = Math.max(1, step);
    top = Math.ceil(max / step) * step;
  }
  const ticks: number[] = [];
  for (let t = 0; t <= top + 1e-9; t += step) ticks.push(Math.round(t));
  return { max: top, ticks };
}

export interface Delta {
  /** Изменение в процентах, округлённое до целого. */
  pct: number;
  /** «+18%», «−3%», «0%» (минус — типографский). */
  text: string;
  tone: 'pos' | 'neg' | 'neutral';
}

/**
 * Изменение «текущее к прошлому» в процентах. Без базы (прошлое ≤ 0 или нет данных) — null:
 * процент от нуля не показываем, чтобы не выдумывать рост.
 */
export function percentDelta(current: number | null | undefined, previous: number | null | undefined): Delta | null {
  if (typeof current !== 'number' || typeof previous !== 'number') return null;
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { pct: 0, text: '0%', tone: 'neutral' };
  return { pct, text: `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`, tone: pct > 0 ? 'pos' : 'neg' };
}
